// ─── CMS Management: the website's settings as data ──────────────────────
//
// Pure helpers behind the CMS Management screens — no React, no requests —
// so what is saved, what is validated and what the setup checklist claims can
// be tested on their own.
//
// Two stores make up the website's settings:
//   • page content (`/cms/page-content`) — copy and images, keyed by page
//   • customisation (`/institute/cms-customization`) — header, footer,
//     appearance and custom code, which the API accepts field by field
// `normalizeCustomization` and `customizationPayload` are the two directions
// of the second one; the field list mirrors updateCmsCustomization in
// vc_be/controllers/instituteController.js, which drops anything else.

// Layout presets. Mirrors CMS_LAYOUT_PRESETS (vc_be instituteController) and
// ALLOWED_PRESETS (vc_cms themes/registry.js) — a preset is added in all three.
export const LAYOUT_PRESETS = [
  {
    id: 'default',
    label: 'Classic',
    tagline: 'Original platform layout',
    description: 'Bold headline over a dark hero, a course grid and a sticky header. Suits most institutes.',
  },
  {
    id: 'theme-2',
    label: 'Editorial',
    tagline: 'Warm, magazine-style',
    description: 'Fraunces headlines on DM Sans, a terracotta accent and a bento-grid hero.',
  },
  {
    id: 'theme-3',
    label: 'EdTech Sales',
    tagline: 'Conversion-focused',
    description: 'Warm paper with a teal accent, a scrolling course rail and demo/counsellor calls to action throughout. Adds Placements and Jobs pages; has no About, FAQs, Gallery or Testimonials pages.',
    // theme-3 draws its header and footer from its own page content
    // (t3_global), not from the Header & Footer settings.
    ownChrome: true,
  },
];

export const layoutLabel = (id) => (LAYOUT_PRESETS.find((p) => p.id === id) || LAYOUT_PRESETS[0]).label;
export const layoutHasOwnChrome = (id) => !!LAYOUT_PRESETS.find((p) => p.id === id)?.ownChrome;

export const BRANDING_MODES = [
  {
    id: 'preset',
    label: 'The layout’s own colours and fonts',
    description: 'Closest to the design as shipped. Recommended when you first pick a layout.',
  },
  {
    id: 'institute',
    label: 'My brand colour and font',
    description: 'Uses the primary colour from Settings → General and the font chosen below. Some layouts match their design less closely this way.',
  },
];

export const FONT_PRESET_OPTIONS = [
  { value: 'Inter', label: 'Inter' },
  { value: 'DM Sans', label: 'DM Sans' },
  { value: 'Poppins', label: 'Poppins' },
  { value: 'Open Sans', label: 'Open Sans' },
  { value: 'Lato', label: 'Lato' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'System UI', label: 'System UI (no web font)' },
  { value: 'Custom', label: 'Custom (CSS font-family)' },
];

export const HERO_TITLE_SCALES = [
  { value: '1', label: 'Default (100%)' },
  { value: '0.9', label: 'Smaller (90%)' },
  { value: '0.8', label: 'Small (80%)' },
  { value: '0.7', label: 'Compact (70%)' },
  { value: '0.6', label: 'Extra compact (60%)' },
];

// ── Menus ─────────────────────────────────────────────────────────────────
// Until an institute saves a menu, the website builds it from these routes
// and the older per-link labels in `global` (navLinkHome, …). The editor
// starts from the same list, so what it shows is what the site shows.
const link = (href, label, extra = {}) => ({ href, label, enabled: true, openInNewTab: false, ...extra });

export const defaultNavLinks = (g = {}) => [
  link('/', g.navLinkHome || 'Home', { icon: '' }),
  link('/courses', g.navLinkCourses || 'Courses', { icon: '' }),
  link('/blogs', g.navLinkBlogs || 'Blogs', { icon: '' }),
  link('/about', g.navLinkAbout || 'About', { icon: '' }),
  link('/contact', g.navLinkContact || 'Contact', { icon: '' }),
];

export const defaultMoreLinks = (g = {}) => [
  link('/testimonials', g.navLinkTestimonials || 'Testimonials', { icon: 'bi-chat-quote' }),
  link('/gallery', g.navLinkGallery || 'Gallery', { icon: 'bi-images' }),
  link('/faqs', g.navLinkFaqs || 'FAQs', { icon: 'bi-question-circle' }),
];

export const defaultFooterLinks = (g = {}) => [
  link('/', g.navLinkHome || 'Home'),
  link('/courses', g.navLinkCourses || 'Courses'),
  link('/blogs', g.navLinkBlogs || 'Blogs'),
  link('/about', g.navLinkAbout || 'About'),
  link('/testimonials', g.navLinkTestimonials || 'Testimonials'),
  link('/gallery', g.navLinkGallery || 'Gallery'),
  link('/faqs', g.navLinkFaqs || 'FAQs'),
  link('/contact', g.navLinkContact || 'Contact'),
];

const cleanLinks = (rows, withIcon) => (Array.isArray(rows) ? rows : [])
  .filter((l) => l && String(l.href || '').trim() && String(l.label || '').trim())
  .map((l) => ({
    href: String(l.href).trim(),
    label: String(l.label).trim(),
    enabled: l.enabled !== false,
    openInNewTab: !!l.openInNewTab,
    ...(withIcon ? { icon: String(l.icon || '').trim() } : {}),
  }));

// ── Customisation: API ⇄ editor state ────────────────────────────────────
const str = (v, fallback = '') => (typeof v === 'string' ? v : fallback);
const num = (v, fallback) => (Number.isFinite(Number(v)) && v !== null && v !== '' ? Number(v) : fallback);
const bool = (v, fallback) => (typeof v === 'boolean' ? v : fallback);
// "Show the brand name beside the logo" defaults differently per layout
// (Editorial shows it, Classic does not), so an unset value stays null here
// and is resolved against the layout where it is displayed — writing
// Classic's `false` for an Editorial site would hide its brand name.
const optionalBool = (v) => (typeof v === 'boolean' ? v : null);

/** The value a layout renders for an unset `showBrandNameWithLogo`. */
export const brandNameDefault = (layoutPreset) => layoutPreset === 'theme-2';

/**
 * GET /institute/cms-customization → editor state, every field present.
 * `global` is the page content's `global` block, used to seed unsaved menus.
 */
export function normalizeCustomization(data = {}, global = {}) {
  const n = data.navbar || {};
  const f = data.footer || {};
  const t = data.theme || {};
  const c = data.customCode || {};
  return {
    navbar: {
      logoOverHeroS3Key: str(n.logoOverHeroS3Key),
      logoOverHeroExternalUrl: str(n.logoOverHeroExternalUrl),
      logoOverHeroPreviewUrl: str(n.logoOverHeroPreviewUrl),
      logoScrolledBarS3Key: str(n.logoScrolledBarS3Key),
      logoScrolledBarExternalUrl: str(n.logoScrolledBarExternalUrl),
      logoScrolledBarPreviewUrl: str(n.logoScrolledBarPreviewUrl),
      logoMaxHeightDesktop: num(n.logoMaxHeightDesktop, 50),
      logoMaxHeightTablet: num(n.logoMaxHeightTablet, 45),
      logoMaxHeightMobile: num(n.logoMaxHeightMobile, 40),
      showBrandNameWithLogo: optionalBool(n.showBrandNameWithLogo),
      stickyOnScroll: bool(n.stickyOnScroll, true),
      showGetStarted: bool(n.showGetStarted, true),
      getStartedText: str(n.getStartedText, 'Get Started') || 'Get Started',
      getStartedLink: str(n.getStartedLink, '/contact') || '/contact',
      getStartedOpenInNewTab: bool(n.getStartedOpenInNewTab, false),
      showBookDemo: bool(n.showBookDemo, true),
      bookDemoText: str(n.bookDemoText, 'Book Demo') || 'Book Demo',
      hideMoreDropdown: bool(n.hideMoreDropdown, false),
      navLinks: Array.isArray(n.navLinks) && n.navLinks.length ? n.navLinks : defaultNavLinks(global),
      moreLinks: Array.isArray(n.moreLinks) && n.moreLinks.length ? n.moreLinks : defaultMoreLinks(global),
    },
    footer: {
      logoBrandS3Key: str(f.logoBrandS3Key),
      logoBrandExternalUrl: str(f.logoBrandExternalUrl),
      logoBrandPreviewUrl: str(f.logoBrandPreviewUrl),
      logoBottomBarS3Key: str(f.logoBottomBarS3Key),
      logoBottomBarExternalUrl: str(f.logoBottomBarExternalUrl),
      logoBottomBarPreviewUrl: str(f.logoBottomBarPreviewUrl),
      logoMaxHeightDesktop: num(f.logoMaxHeightDesktop, 60),
      logoMaxHeightTablet: num(f.logoMaxHeightTablet, 50),
      logoMaxHeightMobile: num(f.logoMaxHeightMobile, 40),
      showBrandNameWithLogo: optionalBool(f.showBrandNameWithLogo),
      copyrightText: str(f.copyrightText),
      hideQuickLinks: bool(f.hideQuickLinks, false),
      hidePopularCourses: bool(f.hidePopularCourses, false),
      hideContactInfo: bool(f.hideContactInfo, false),
      hideSocialLinks: bool(f.hideSocialLinks, false),
      quickLinks: Array.isArray(f.quickLinks) && f.quickLinks.length ? f.quickLinks : defaultFooterLinks(global),
    },
    theme: {
      heroGradient1: str(t.heroGradient1),
      heroGradient2: str(t.heroGradient2),
      heroGradient3: str(t.heroGradient3),
      heroGradient4: str(t.heroGradient4),
      heroBackgroundImageKey: str(t.heroBackgroundImageKey),
      heroBackgroundImageExternalUrl: str(t.heroBackgroundImageExternalUrl),
      heroBackgroundImagePreviewUrl: str(t.heroBackgroundImagePreviewUrl),
      siteFontPreset: str(t.siteFontPreset, 'Inter') || 'Inter',
      siteFontCustom: str(t.siteFontCustom),
      heroTitleScale: num(t.heroTitleScale, 1),
      hideCourseMoneyBack: bool(t.hideCourseMoneyBack, false),
      hideCourseLifetimeAccess: bool(t.hideCourseLifetimeAccess, false),
    },
    customCode: {
      customCss: str(c.customCss),
      customHeadScripts: str(c.customHeadScripts),
      customBodyScripts: str(c.customBodyScripts),
    },
  };
}

/** Editor state → PUT /institute/cms-customization body. Preview URLs are display-only. */
export function customizationPayload(state) {
  const { navbar: n, footer: f, theme: t, customCode: c } = state;
  return {
    customCode: { ...c },
    navbar: {
      logoOverHeroS3Key: n.logoOverHeroS3Key.trim(),
      logoOverHeroExternalUrl: n.logoOverHeroExternalUrl.trim(),
      logoScrolledBarS3Key: n.logoScrolledBarS3Key.trim(),
      logoScrolledBarExternalUrl: n.logoScrolledBarExternalUrl.trim(),
      logoMaxHeightDesktop: n.logoMaxHeightDesktop,
      logoMaxHeightTablet: n.logoMaxHeightTablet,
      logoMaxHeightMobile: n.logoMaxHeightMobile,
      ...(n.showBrandNameWithLogo === null ? {} : { showBrandNameWithLogo: n.showBrandNameWithLogo }),
      stickyOnScroll: n.stickyOnScroll,
      showGetStarted: n.showGetStarted,
      getStartedText: n.getStartedText,
      getStartedLink: n.getStartedLink,
      getStartedOpenInNewTab: n.getStartedOpenInNewTab,
      showBookDemo: n.showBookDemo,
      bookDemoText: n.bookDemoText,
      hideMoreDropdown: n.hideMoreDropdown,
      navLinks: cleanLinks(n.navLinks, true),
      moreLinks: cleanLinks(n.moreLinks, true),
    },
    footer: {
      logoBrandS3Key: f.logoBrandS3Key.trim(),
      logoBrandExternalUrl: f.logoBrandExternalUrl.trim(),
      logoBottomBarS3Key: f.logoBottomBarS3Key.trim(),
      logoBottomBarExternalUrl: f.logoBottomBarExternalUrl.trim(),
      logoMaxHeightDesktop: f.logoMaxHeightDesktop,
      logoMaxHeightTablet: f.logoMaxHeightTablet,
      logoMaxHeightMobile: f.logoMaxHeightMobile,
      ...(f.showBrandNameWithLogo === null ? {} : { showBrandNameWithLogo: f.showBrandNameWithLogo }),
      copyrightText: f.copyrightText,
      hideQuickLinks: f.hideQuickLinks,
      hidePopularCourses: f.hidePopularCourses,
      hideContactInfo: f.hideContactInfo,
      hideSocialLinks: f.hideSocialLinks,
      quickLinks: cleanLinks(f.quickLinks, false),
    },
    theme: {
      heroGradient1: t.heroGradient1.trim(),
      heroGradient2: t.heroGradient2.trim(),
      heroGradient3: t.heroGradient3.trim(),
      heroGradient4: t.heroGradient4.trim(),
      heroBackgroundImageKey: t.heroBackgroundImageKey.trim(),
      heroBackgroundImageExternalUrl: t.heroBackgroundImageExternalUrl.trim(),
      siteFontPreset: t.siteFontPreset,
      siteFontCustom: t.siteFontCustom.trim(),
      heroTitleScale: t.heroTitleScale,
      hideCourseMoneyBack: t.hideCourseMoneyBack,
      hideCourseLifetimeAccess: t.hideCourseLifetimeAccess,
    },
  };
}

const HEX_RE = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

const isHttpUrl = (s) => {
  try {
    const u = new URL(s);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

const halfFilledLink = (rows) => (Array.isArray(rows) ? rows : []).some((l) => {
  const hasLabel = !!String(l?.label || '').trim();
  const hasHref = !!String(l?.href || '').trim();
  return hasLabel !== hasHref;
});

/**
 * Problems that would make the save fail or silently drop something, keyed by
 * where the editor shows them. Empty object → safe to save.
 */
export function validateCustomization(state) {
  const errors = {};
  const { navbar: n, footer: f, theme: t } = state;

  if (t.siteFontPreset === 'Custom' && !t.siteFontCustom.trim()) {
    errors['theme.siteFontCustom'] = 'Enter a CSS font-family, or pick a preset.';
  }
  const gradient = [t.heroGradient1, t.heroGradient2, t.heroGradient3, t.heroGradient4].map((g) => g.trim());
  const filled = gradient.filter(Boolean);
  if (filled.length > 0 && filled.length < 4) {
    errors['theme.heroGradient'] = 'Set all four colours, or clear them all to use the default.';
  } else if (filled.some((g) => !HEX_RE.test(g))) {
    errors['theme.heroGradient'] = 'Colours must be written as #RGB or #RRGGBB.';
  }

  const urlFields = [
    ['theme.heroBackgroundImageExternalUrl', t.heroBackgroundImageExternalUrl],
    ['navbar.logoOverHeroExternalUrl', n.logoOverHeroExternalUrl],
    ['navbar.logoScrolledBarExternalUrl', n.logoScrolledBarExternalUrl],
    ['footer.logoBrandExternalUrl', f.logoBrandExternalUrl],
    ['footer.logoBottomBarExternalUrl', f.logoBottomBarExternalUrl],
  ];
  urlFields.forEach(([key, value]) => {
    const v = String(value || '').trim();
    if (v && !isHttpUrl(v)) errors[key] = 'Use a full web address starting with https://';
  });

  const LINK_MESSAGE = 'Every link needs both a label and an address — half-filled rows are not saved.';
  if (halfFilledLink(n.navLinks)) errors['navbar.navLinks'] = LINK_MESSAGE;
  if (halfFilledLink(n.moreLinks)) errors['navbar.moreLinks'] = LINK_MESSAGE;
  if (halfFilledLink(f.quickLinks)) errors['footer.quickLinks'] = LINK_MESSAGE;

  return errors;
}

// ── Page content helpers ──────────────────────────────────────────────────

/** Whether an admin actually filled a value in (a toggle is a setting, not content). */
export function isFilled(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (Array.isArray(value)) {
    return value.some((row) => (row && typeof row === 'object'
      ? Object.values(row).some((v) => typeof v === 'string' && v.trim())
      : isFilled(row)));
  }
  if (typeof value === 'object') return Object.values(value).some(isFilled);
  return false;
}

/** How many of a section's fields hold the admin's own content. */
export const countFilledFields = (fields, pageContent = {}) =>
  fields.filter((f) => f.type !== 'toggle' && isFilled(pageContent?.[f.key])).length;

/**
 * Section show/hide toggle state — absent means shown, as on the website.
 *
 * Except an `optIn` section (`visibility: { key, optIn: true }`): one added to
 * a page after live sites were built on it, where absent must mean HIDDEN or
 * every existing site would grow the section on deploy. The home page's
 * Trainers & mentors is the first; vc_cms reads it with the same rule
 * (lib/team.homeTeamOn).
 */
export const isSectionShown = (pageContent, key, { optIn = false } = {}) => {
  const v = pageContent?.[key];
  if (optIn) return v === true || v === 'true';
  return v !== false && v !== 'false';
};

/**
 * Where the public website lives. An institute on its own domain serves the
 * website there and the LMS elsewhere, so the current origin would open the
 * LMS; everyone else is served both from one host.
 */
export function publicSiteUrl(institute, path = '/') {
  const domain = String(institute?.customDomain || '').trim();
  const base = domain ? `https://${domain}` : window.location.origin;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

// ── Setup checklist ───────────────────────────────────────────────────────
// Each item points at the editor that completes it. `target` is interpreted
// by CmsManagement: `{ tab }` or `{ tab: 'pages', page, section }`.

const SOCIAL_KEYS = [
  'socialFacebook', 'socialTwitter', 'socialLinkedin', 'socialInstagram',
  'socialYoutube', 'socialWhatsapp', 'socialTelegram',
];

export function buildSetupChecklist({ layoutPreset, content = {}, stats = {} }) {
  const home = content.home || {};
  const items = [];

  if (!layoutHasOwnChrome(layoutPreset)) {
    items.push(
      {
        id: 'hero-image',
        label: 'Replace the sample hero photo',
        detail: 'The home page opens with a stock photo until you upload your own.',
        done: isFilled(home.heroImage),
        target: { tab: 'pages', page: 'home', section: 'hero' },
      },
      {
        id: 'stats',
        label: 'Add your own numbers',
        detail: 'Sample figures such as “5,000+ Students Trained” are shown until you do.',
        done: isFilled(home.stats),
        target: { tab: 'pages', page: 'home', section: 'stats' },
      },
      {
        id: 'featured-courses',
        label: 'Choose the courses to feature',
        detail: 'Without a choice, the home page shows your newest courses.',
        done: isFilled(home.featuredCourses),
        target: { tab: 'pages', page: 'home', section: 'courses' },
      },
      {
        id: 'faqs',
        label: 'Answer common questions',
        detail: 'The FAQ section and the FAQs page stay empty until you add some.',
        done: isFilled(home.faqs) || isFilled(content.faqs?.items),
        target: { tab: 'pages', page: 'home', section: 'faq' },
      },
      {
        id: 'contact',
        label: 'Add your contact details',
        detail: 'Shown on the Contact page and in the footer.',
        done: isFilled(content.contact?.phonePrimary) || isFilled(content.contact?.emailPrimary),
        target: { tab: 'pages', page: 'contact', section: 'details' },
      },
      {
        id: 'gallery',
        label: 'Add gallery photos',
        detail: 'The Gallery page says “coming soon” until it has photos.',
        done: isFilled(content.gallery?.images),
        target: { tab: 'pages', page: 'gallery', section: 'photos' },
      },
      {
        id: 'social',
        label: 'Link your social profiles',
        detail: 'Icons appear in the footer for each profile you add.',
        done: SOCIAL_KEYS.some((k) => isFilled(content.global?.[k])),
        target: { tab: 'header-footer' },
      },
    );
  }

  items.push(
    {
      id: 'team',
      label: 'Introduce your mentors and trainers',
      detail: 'Visitors want to know who teaches. A profile here is website-only — it gives nobody LMS access.',
      done: (stats.visibleTeam || 0) > 0,
      target: { tab: 'team' },
    },
    {
      id: 'testimonials',
      label: 'Add student testimonials',
      detail: 'Real stories are the strongest proof on the site.',
      done: (stats.totalTestimonials || 0) > 0,
      target: { tab: 'testimonials' },
    },
    {
      id: 'blog',
      label: 'Publish your first blog post',
      detail: 'Fresh posts help your site show up in search.',
      done: (stats.publishedBlogs || 0) > 0,
      target: { tab: 'blogs' },
    },
  );
  return items;
}
